import { useState, useCallback, useEffect } from 'react';
import { ArrowLeft, Save, X, Calendar, MapPin, Users, Heart, Palette, Crown } from 'lucide-react';
import { WeddingInvitation } from '../types/invitation';
import { invitationService } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { useSubscription } from '../contexts/SubscriptionContext';
import { useSEO } from '../hooks/useSEO';
import { generateEditTitle, generateMetaDescription } from '../lib/brandUtils';
import { createLogger } from '../lib/logger';

const log = createLogger('invitation');

interface EditInvitationPageProps {
  invitation?: WeddingInvitation;
  inviteCode?: string;
  onSave?: (updatedInvitation: WeddingInvitation) => void;
  onCancel?: () => void;
  onBack?: () => void;
}

// 將 InputField 組件移到外部，避免重新渲染導致失去焦點
const InputField: React.FC<{
  label: string;
  field: string;
  value: string;
  type?: string;
  placeholder?: string;
  error?: string;
  onChange: (field: string, value: string) => void;
}> = ({ label, field, value, type = 'text', placeholder = '', error, onChange }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-2">
      {label} <span className="text-red-500">*</span>
    </label>
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(field, e.target.value)}
      className={`w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-pink-500 ${
        error ? 'border-red-500' : 'border-gray-300'
      }`}
      placeholder={placeholder}
    />
    {error && (
      <p className="text-red-500 text-sm mt-1">{error}</p>
    )}
  </div>
);

export const EditInvitationPage: React.FC<EditInvitationPageProps> = ({
  invitation,
  inviteCode,
  onSave,
  onCancel,
  onBack
}) => {
  const { user } = useAuth();
  const { canCustomizeBranding } = useSubscription();
  const [currentInvitation, setCurrentInvitation] = useState<WeddingInvitation | null>(invitation || null);
  const [loading, setLoading] = useState(!invitation && !!inviteCode);
  
  // 如果只提供了 inviteCode，需要加载邀请函数据
  useEffect(() => {
    const loadInvitation = async () => {
      if (!inviteCode || invitation) return;
      
      try {
        setLoading(true);
        const loadedInvitation = await invitationService.getInvitationByCode(inviteCode);
        if (loadedInvitation) {
          setCurrentInvitation(loadedInvitation);
        }
      } catch (error) {
        log.error('Error loading invitation:', error);
      } finally {
        setLoading(false);
      }
    };

    loadInvitation();
  }, [inviteCode, invitation]);

  // SEO 優化
  const seoTitle = currentInvitation 
    ? generateEditTitle(currentInvitation.brideName, currentInvitation.groomName)
    : generateEditTitle();

  useSEO({
    title: seoTitle,
    description: generateMetaDescription('編輯您的婚禮邀請函，自訂新人資訊、婚禮地點和時間等詳細資訊'),
    keywords: '婚禮邀請函編輯, 婚禮規劃, 邀請函設計, 婚宴資訊'
  });

  const [formData, setFormData] = useState({
    brideName: '',
    groomName: '',
    weddingDate: '',
    weddingTime: '',
    venue: '',
    address: '',
    contactPhone: '',
    contactEmail: '',
    welcomeMessage: '',
    coverImage: '',
    // 活動選項
    hasWelcomeParty: false,
    welcomePartyTime: '',
    welcomePartyVenue: '',
    welcomePartyAddress: '',
    hasWeddingCeremony: false,
    ceremonyTime: '',
    ceremonyVenue: '',
    ceremonyAddress: '',
    hasReception: false,
    receptionTime: '',
    receptionVenue: '',
    receptionAddress: '',
    showTransportInfo: false,
    transportDetails: '',
    showContactInfo: false,
    // RSVP 控制
    acceptSubmissions: true,
    footerBranding: ''
  });

  // 當邀請函資料載入後，更新表單資料
  useEffect(() => {
    if (currentInvitation) {
      setFormData({
        brideName: currentInvitation.brideName || '',
        groomName: currentInvitation.groomName || '',
        weddingDate: currentInvitation.weddingDate || '',
        weddingTime: currentInvitation.weddingTime || '',
        venue: currentInvitation.venue || '',
        address: currentInvitation.address || '',
        contactPhone: currentInvitation.contactPhone || '',
        contactEmail: currentInvitation.contactEmail || '',
        welcomeMessage: currentInvitation.welcomeMessage || '',
        coverImage: currentInvitation.coverImage || '',
        // 活動選項 - 向下兼容，如果欄位不存在則顯示為未勾選
        hasWelcomeParty: currentInvitation.hasWelcomeParty ?? false,
        welcomePartyTime: currentInvitation.welcomePartyTime || '',
        welcomePartyVenue: currentInvitation.welcomePartyVenue || '',
        welcomePartyAddress: currentInvitation.welcomePartyAddress || '',
        hasWeddingCeremony: currentInvitation.hasWeddingCeremony ?? true, // 預設為 true 以兼容舊資料
        ceremonyTime: currentInvitation.ceremonyTime || '',
        ceremonyVenue: currentInvitation.ceremonyVenue || '',
        ceremonyAddress: currentInvitation.ceremonyAddress || '',
        hasReception: currentInvitation.hasReception ?? true, // 預設為 true 以兼容舊資料
        receptionTime: currentInvitation.receptionTime || '',
        receptionVenue: currentInvitation.receptionVenue || '',
        receptionAddress: currentInvitation.receptionAddress || '',
        showTransportInfo: currentInvitation.showTransportInfo ?? false,
        transportDetails: currentInvitation.transportDetails || '',
        showContactInfo: currentInvitation.showContactInfo ?? false,
        // RSVP 控制
        acceptSubmissions: currentInvitation.acceptSubmissions ?? true,
        footerBranding: currentInvitation.footerBranding || ''
      });
    }
  }, [currentInvitation]);

  const [errors, setErrors] = useState<Record<string, string>>({});

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    
    if (!formData.brideName.trim()) newErrors.brideName = '請輸入新娘姓名';
    if (!formData.groomName.trim()) newErrors.groomName = '請輸入新郎姓名';
    if (!formData.weddingDate.trim()) newErrors.weddingDate = '請輸入婚禮日期';
    if (!formData.weddingTime.trim()) newErrors.weddingTime = '請輸入婚禮時間';
    if (!formData.venue.trim()) newErrors.venue = '請輸入場地名稱';
    if (!formData.address.trim()) newErrors.address = '請輸入場地地址';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!currentInvitation) return;
    
    if (validateForm()) {
      const updatedInvitation: WeddingInvitation = {
        ...currentInvitation,
        brideName: formData.brideName,
        groomName: formData.groomName,
        weddingDate: formData.weddingDate,
        weddingTime: formData.weddingTime,
        venue: formData.venue,
        address: formData.address,
        contactPhone: formData.contactPhone,
        contactEmail: formData.contactEmail,
        welcomeMessage: formData.welcomeMessage,
        coverImage: formData.coverImage,
        // 活動選項
        hasWelcomeParty: formData.hasWelcomeParty,
        welcomePartyTime: formData.welcomePartyTime,
        welcomePartyVenue: formData.welcomePartyVenue,
        welcomePartyAddress: formData.welcomePartyAddress,
        hasWeddingCeremony: formData.hasWeddingCeremony,
        ceremonyTime: formData.ceremonyTime,
        ceremonyVenue: formData.ceremonyVenue,
        ceremonyAddress: formData.ceremonyAddress,
        hasReception: formData.hasReception,
        receptionTime: formData.receptionTime,
        receptionVenue: formData.receptionVenue,
        receptionAddress: formData.receptionAddress,
        showTransportInfo: formData.showTransportInfo,
        transportDetails: formData.transportDetails,
        showContactInfo: formData.showContactInfo,
        // RSVP 控制
        acceptSubmissions: formData.acceptSubmissions,
        footerBranding: formData.footerBranding || undefined
      };
      
      if (onSave) {
        onSave(updatedInvitation);
      } else if (user?.id) {
        // 如果沒有 onSave 回調，直接更新到資料庫
        try {
          await invitationService.updateInvitation(currentInvitation.id, updatedInvitation);
          alert('邀請函已成功更新！');
          if (onBack) onBack();
        } catch (error) {
          log.error('Error updating invitation:', error);
          alert('更新失敗，請稍後再試');
        }
      }
    }
  };

  const updateField = useCallback((field: string, value: string | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // 清除該欄位的錯誤
    setErrors(prev => {
      if (prev[field]) {
        return { ...prev, [field]: '' };
      }
      return prev;
    });
  }, []);


  // 載入狀態
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-pink-50 to-purple-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-500 mx-auto mb-4"></div>
          <p className="text-gray-600">載入邀請函資料中...</p>
        </div>
      </div>
    );
  }

  // 如果沒有邀請函資料
  if (!currentInvitation) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-pink-50 to-purple-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600 mb-4">找不到邀請函資料</p>
          <button
            onClick={onBack || onCancel}
            className="bg-pink-500 hover:bg-pink-600 text-white px-4 py-2 rounded-lg"
          >
            返回
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 to-purple-50">
      {/* 頂部導航 */}
      <div className="bg-white shadow-sm">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <button
                onClick={onBack || onCancel}
                className="flex items-center text-gray-600 hover:text-gray-800 mr-4"
              >
                <ArrowLeft className="h-5 w-5 mr-2" />
                返回
              </button>
              <h1 className="text-2xl font-bold text-gray-900">編輯邀請函</h1>
            </div>
            <div className="flex gap-3">
              <button
                onClick={onBack || onCancel}
                className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-lg font-medium transition-colors flex items-center"
              >
                <X className="h-4 w-4 mr-2" />
                取消
              </button>
              <button
                onClick={handleSave}
                className="bg-pink-500 hover:bg-pink-600 text-white px-4 py-2 rounded-lg font-medium transition-colors flex items-center"
              >
                <Save className="h-4 w-4 mr-2" />
                保存變更
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* 編輯表單 */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-white rounded-lg shadow-lg p-8">
          {/* 新人資訊 */}
          <div className="mb-8">
            <h2 className="text-xl font-semibold mb-6 flex items-center">
              <Heart className="h-6 w-6 text-pink-500 mr-3" />
              新人資訊
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              <InputField
                label="新娘姓名"
                field="brideName"
                value={formData.brideName}
                placeholder="請輸入新娘姓名"
                error={errors.brideName}
                onChange={updateField}
              />
              <InputField
                label="新郎姓名"
                field="groomName"
                value={formData.groomName}
                placeholder="請輸入新郎姓名"
                error={errors.groomName}
                onChange={updateField}
              />
            </div>
          </div>

          {/* 婚禮詳情 */}
          <div className="mb-8">
            <h2 className="text-xl font-semibold mb-6 flex items-center">
              <Calendar className="h-6 w-6 text-pink-500 mr-3" />
              婚禮詳情
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              <InputField
                label="婚禮日期"
                field="weddingDate"
                value={formData.weddingDate}
                type="date"
                error={errors.weddingDate}
                onChange={updateField}
              />
              <InputField
                label="婚禮時間"
                field="weddingTime"
                value={formData.weddingTime}
                type="time"
                error={errors.weddingTime}
                onChange={updateField}
              />
            </div>
          </div>

          {/* 場地資訊 */}
          <div className="mb-8">
            <h2 className="text-xl font-semibold mb-6 flex items-center">
              <MapPin className="h-6 w-6 text-pink-500 mr-3" />
              場地資訊
            </h2>
            <div className="space-y-6">
              <InputField
                label="場地名稱"
                field="venue"
                value={formData.venue}
                placeholder="例如：台北晶華酒店"
                error={errors.venue}
                onChange={updateField}
              />
              <InputField
                label="場地地址"
                field="address"
                value={formData.address}
                placeholder="例如：台北市中山區中山北路二段39巷3號"
                error={errors.address}
                onChange={updateField}
              />
            </div>
          </div>

          {/* 活動安排 */}
          <div className="mb-8">
            <h2 className="text-xl font-semibold mb-6 flex items-center">
              <Calendar className="h-6 w-6 text-pink-500 mr-3" />
              活動安排
            </h2>
            <div className="space-y-6">
              {/* 迎賓 */}
              <div className="border border-gray-200 rounded-lg p-4">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.hasWelcomeParty}
                    onChange={(e) => updateField('hasWelcomeParty', e.target.checked)}
                    className="mr-3 h-4 w-4 text-pink-600 focus:ring-pink-500 border-gray-300 rounded"
                  />
                  <span className="text-sm font-medium text-gray-900">迎賓</span>
                </label>
                {formData.hasWelcomeParty && (
                  <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4 pl-7">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">時間</label>
                      <input
                        type="time"
                        value={formData.welcomePartyTime}
                        onChange={(e) => updateField('welcomePartyTime', e.target.value)}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">地點（如與主要地點不同）</label>
                      <input
                        type="text"
                        value={formData.welcomePartyVenue}
                        onChange={(e) => updateField('welcomePartyVenue', e.target.value)}
                        placeholder="留空表示與主要地點相同"
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* 證婚儀式 */}
              <div className="border border-gray-200 rounded-lg p-4">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.hasWeddingCeremony}
                    onChange={(e) => updateField('hasWeddingCeremony', e.target.checked)}
                    className="mr-3 h-4 w-4 text-pink-600 focus:ring-pink-500 border-gray-300 rounded"
                  />
                  <span className="text-sm font-medium text-gray-900">證婚儀式</span>
                </label>
                {formData.hasWeddingCeremony && (
                  <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4 pl-7">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">時間</label>
                      <input
                        type="time"
                        value={formData.ceremonyTime}
                        onChange={(e) => updateField('ceremonyTime', e.target.value)}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">地點（如與主要地點不同）</label>
                      <input
                        type="text"
                        value={formData.ceremonyVenue}
                        onChange={(e) => updateField('ceremonyVenue', e.target.value)}
                        placeholder="留空表示與主要地點相同"
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* 婚宴 */}
              <div className="border border-gray-200 rounded-lg p-4">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.hasReception}
                    onChange={(e) => updateField('hasReception', e.target.checked)}
                    className="mr-3 h-4 w-4 text-pink-600 focus:ring-pink-500 border-gray-300 rounded"
                  />
                  <span className="text-sm font-medium text-gray-900">婚宴</span>
                </label>
                {formData.hasReception && (
                  <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4 pl-7">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">時間</label>
                      <input
                        type="time"
                        value={formData.receptionTime}
                        onChange={(e) => updateField('receptionTime', e.target.value)}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">地點（如與主要地點不同）</label>
                      <input
                        type="text"
                        value={formData.receptionVenue}
                        onChange={(e) => updateField('receptionVenue', e.target.value)}
                        placeholder="留空表示與主要地點相同"
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* 賓客服務選項 */}
              <div className="border-t border-gray-200 pt-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">賓客服務選項</h3>
                <div className="space-y-4">
                  {/* 交通資訊 */}
                  <div className="border border-gray-200 rounded-lg p-4">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={formData.showTransportInfo}
                        onChange={(e) => updateField('showTransportInfo', e.target.checked)}
                        className="mr-3 h-4 w-4 text-pink-600 focus:ring-pink-500 border-gray-300 rounded"
                      />
                      <span className="text-sm font-medium text-gray-900">提供交通資訊</span>
                    </label>
                    <p className="text-xs text-gray-500 mt-1 pl-7">在邀請函中顯示如何到達婚禮場地的交通資訊</p>
                    {formData.showTransportInfo && (
                      <div className="mt-4 pl-7">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          交通資訊詳細內容
                        </label>
                        <textarea
                          value={formData.transportDetails}
                          onChange={(e) => updateField('transportDetails', e.target.value)}
                          placeholder="請詳細說明如何到達婚禮場地的交通方式..."
                          rows={3}
                          className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                        />
                        <p className="text-xs text-gray-500 mt-1">留空將使用預設的交通資訊範本</p>
                      </div>
                    )}
                  </div>

                  {/* 聯絡資訊 */}
                  <div className="border border-gray-200 rounded-lg p-4">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={formData.showContactInfo}
                        onChange={(e) => updateField('showContactInfo', e.target.checked)}
                        className="mr-3 h-4 w-4 text-pink-600 focus:ring-pink-500 border-gray-300 rounded"
                      />
                      <span className="text-sm font-medium text-gray-900">提供聯絡方式</span>
                    </label>
                    <p className="text-xs text-gray-500 mt-1 pl-7">讓賓客能夠聯絡您詢問相關問題</p>
                  </div>

                  {/* RSVP 提交控制 */}
                  <div className="border border-gray-200 rounded-lg p-4">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={formData.acceptSubmissions ?? true}
                        onChange={(e) => updateField('acceptSubmissions', e.target.checked)}
                        className="mr-3 h-4 w-4 text-pink-600 focus:ring-pink-500 border-gray-300 rounded"
                      />
                      <span className="text-sm font-medium text-gray-900">接受 RSVP 回覆</span>
                    </label>
                    <p className="text-xs text-gray-500 mt-1 pl-7">
                      取消勾選後，賓客將無法提交新的回覆。如有緊急需求，賓客可直接聯絡您。
                    </p>
                    {!(formData.acceptSubmissions ?? true) && (
                      <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                        <div className="flex">
                          <div className="flex-shrink-0">
                            <X className="h-4 w-4 text-yellow-400" />
                          </div>
                          <div className="ml-2">
                            <p className="text-sm text-yellow-800">
                              RSVP 提交已關閉，賓客看到的邀請函將顯示適當的提示訊息。
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* 聯絡資訊 */}
          <div className="mb-8">
            <h2 className="text-xl font-semibold mb-6 flex items-center">
              <Users className="h-6 w-6 text-pink-500 mr-3" />
              聯絡資訊 <span className="text-sm font-normal text-gray-500 ml-2">(選填)</span>
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  聯絡電話
                </label>
                <input
                  type="tel"
                  value={formData.contactPhone}
                  onChange={(e) => updateField('contactPhone', e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-pink-500"
                  placeholder="例如：0912-345-678"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  聯絡信箱
                </label>
                <input
                  type="email"
                  value={formData.contactEmail}
                  onChange={(e) => updateField('contactEmail', e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-pink-500"
                  placeholder="例如：wedding@example.com"
                />
              </div>
            </div>
          </div>

          {/* 客製化設定 */}
          <div className="mb-8">
            <h2 className="text-xl font-semibold mb-6 flex items-center">
              <Palette className="h-6 w-6 text-pink-500 mr-3" />
              客製化設定 <span className="text-sm font-normal text-gray-500 ml-2">(選填)</span>
            </h2>
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  歡迎訊息
                </label>
                <textarea
                  value={formData.welcomeMessage}
                  onChange={(e) => updateField('welcomeMessage', e.target.value)}
                  rows={3}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-pink-500"
                  placeholder="例如：誠摯邀請您見證我們的幸福時刻"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  封面圖片網址
                </label>
                <input
                  type="url"
                  value={formData.coverImage}
                  onChange={(e) => updateField('coverImage', e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-pink-500"
                  placeholder="例如：https://example.com/image.jpg"
                />
                {formData.coverImage && (
                  <div className="mt-3">
                    <img
                      src={formData.coverImage}
                      alt="封面預覽"
                      className="w-full h-48 object-cover rounded-lg"
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                      }}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* 自訂品牌 */}
          <div className="mb-8">
            <h2 className="text-xl font-semibold mb-6 flex items-center">
              <Crown className="h-6 w-6 text-wedding-gold mr-3" />
              自訂品牌
            </h2>
            {canCustomizeBranding() ? (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  邀請函底部品牌文字
                </label>
                <input
                  type="text"
                  value={formData.footerBranding || ''}
                  onChange={(e) => updateField('footerBranding', e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-pink-500"
                  placeholder="預設為「101婚禮規劃」"
                />
                <p className="text-sm text-gray-500 mt-1">
                  留空則顯示「101婚禮規劃」
                </p>
              </div>
            ) : (
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <p className="text-sm text-gray-600">
                  升級至進階方案即可自訂邀請函底部品牌文字。
                </p>
              </div>
            )}
          </div>

          {/* 底部行動按鈕 */}
          <div className="flex justify-end gap-4 pt-6 border-t border-gray-200">
            <button
              onClick={onCancel}
              className="bg-gray-500 hover:bg-gray-600 text-white px-6 py-3 rounded-lg font-medium transition-colors"
            >
              取消編輯
            </button>
            <button
              onClick={handleSave}
              className="bg-pink-500 hover:bg-pink-600 text-white px-6 py-3 rounded-lg font-medium transition-colors"
            >
              保存變更
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};